import { useState } from "react";
import { useProfit, type ProfitReport } from "@/api/reports";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { TextField } from "@/components/ui/field";
import { formatDate, formatInr } from "@/lib/format";
import { formatTotals } from "@/lib/money";

/** "Profit" or "Loss" in words, so a negative figure never relies on colour alone. */
export function profitLabel(report: ProfitReport): string {
  return Number(report.profit_inr) < 0 ? "Loss" : "Profit";
}

export function marginText(report: ProfitReport): string {
  return report.margin === null ? "No income yet" : `${report.margin}% margin`;
}

/** The warning when money came in that has no rupee figure, so the profit leaves it out. */
export function NotConvertedAlert({ report }: { report: ProfitReport }) {
  if (report.not_converted.length === 0) return null;
  return (
    <Alert variant="warning" title={`${formatTotals(report.not_converted)} is not counted yet`}>
      It was received without a rupee figure. Add the INR credited (or the rate) on the payment
      and it counts towards income.
    </Alert>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="break-words text-lg font-semibold text-primary">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const TH = "px-4 py-2 font-medium";
const NUM = "whitespace-nowrap px-4 py-2 text-right tabular-nums";

export function ProfitPanel() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const profit = useProfit({ from: from || undefined, to: to || undefined });

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="profit-from"
          label="From"
          type="date"
          value={from}
          onChange={setFrom}
          hint="Leave empty for the start of this financial year"
        />
        <TextField id="profit-to" label="To" type="date" value={to} onChange={setTo} />
      </div>
      {profit.isPending ? (
        <p className="text-muted-foreground">Adding up money in and out…</p>
      ) : profit.isError ? (
        <RequestError error={profit.error} />
      ) : (
        <ProfitFigures report={profit.data} />
      )}
    </div>
  );
}

function ProfitFigures({ report }: { report: ProfitReport }) {
  return (
    <>
      <p className="text-sm text-muted-foreground">
        {formatDate(report.period_from)} to {formatDate(report.period_to)}. Income is the rupees
        the bank credited for payments received; expenses are everything recorded under Expenses.
      </p>
      <Card>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Figure label="Income" value={formatInr(report.income_inr)} />
          <Figure label="Expenses" value={formatInr(report.expenses_inr)} />
          <Figure
            label={profitLabel(report)}
            value={formatInr(report.profit_inr)}
            hint={marginText(report)}
          />
          <Figure
            label="Payouts owed"
            value={formatTotals(report.owed)}
            hint="Clients have paid, the person has not been paid"
          />
        </CardContent>
      </Card>
      <NotConvertedAlert report={report} />

      <div className="table-frame">
        <table className="data-table">
          <caption className="sr-only">Income, expenses and profit by month</caption>
          <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className={TH}>Month</th>
              <th className={`${TH} text-right`}>Income</th>
              <th className={`${TH} text-right`}>Expenses</th>
              <th className={`${TH} text-right`}>Profit</th>
            </tr>
          </thead>
          <tbody>
            {report.months.map((month) => (
              <tr key={month.month} className="border-b last:border-0">
                <td className="px-4 py-2 font-medium">{month.label}</td>
                <td className={NUM}>{formatInr(month.income_inr)}</td>
                <td className={NUM}>{formatInr(month.expenses_inr)}</td>
                <td className={`${NUM} font-medium`}>{formatInr(month.profit_inr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-frame">
        <table className="data-table">
          <caption className="px-4 pt-3 text-left text-sm font-medium">
            By bucket: each bucket's share of the income, less what was spent from it
          </caption>
          <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className={TH}>Bucket</th>
              <th className={`${TH} text-right`}>Share of income</th>
              <th className={`${TH} text-right`}>Spent</th>
              <th className={`${TH} text-right`}>Left</th>
            </tr>
          </thead>
          <tbody>
            {report.buckets.map((bucket) => (
              <tr key={bucket.bucket_id} className="border-b last:border-0">
                <td className="px-4 py-2 font-medium">{bucket.name}</td>
                <td className={NUM}>{formatInr(bucket.income_share_inr)}</td>
                <td className={NUM}>{formatInr(bucket.spent_inr)}</td>
                <td className={`${NUM} font-medium`}>
                  {formatInr(bucket.balance_inr)}
                  {Number(bucket.balance_inr) < 0 && (
                    <span className="block text-xs font-normal text-destructive">
                      Spent ahead of income
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
